import { useEffect } from 'react';
import { Check, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface ShopifyTargetStore {
  id: string;
  nickname?: string | null;
  storeUrl?: string | null;
}

type SelectorVariant = 'select' | 'tiles';

interface ShopifyTargetStoreSelectorProps {
  stores: ShopifyTargetStore[];
  value: string;
  onChange: (storeId: string) => void;
  label?: string;
  placeholder?: string;
  variant?: SelectorVariant;
  disabled?: boolean;
  className?: string;
}

function storeLabel(store: ShopifyTargetStore) {
  return store.nickname || store.storeUrl || 'Shopify store';
}

/**
 * Single-store accounts get a visible, non-interactive summary. Multi-store
 * accounts keep an explicit choice so writes can never silently target the
 * wrong shop.
 */
export function ShopifyTargetStoreSelector({
  stores,
  value,
  onChange,
  label = 'Target Shopify store',
  placeholder = 'Select a store…',
  variant = 'select',
  disabled = false,
  className,
}: ShopifyTargetStoreSelectorProps) {
  useEffect(() => {
    if (stores.length === 1 && value !== stores[0]!.id) {
      onChange(stores[0]!.id);
    } else if (stores.length > 1 && value && !stores.some((store) => store.id === value)) {
      onChange('');
    }
  }, [onChange, stores, value]);

  if (stores.length === 0) return null;

  if (stores.length === 1) {
    const store = stores[0]!;
    return (
      <div className={cn('space-y-1.5', className)} aria-label={`${label}: ${storeLabel(store)}`}>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="flex min-h-9 items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
          <Store className="h-4 w-4 text-muted-foreground" />
          <span className="truncate">{storeLabel(store)}</span>
          {store.nickname && store.storeUrl && (
            <span className="truncate text-xs text-muted-foreground">({store.storeUrl})</span>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'tiles') {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {stores.map((store) => {
            const selected = value === store.id;
            return (
              <button
                key={store.id}
                type="button"
                disabled={disabled}
                onClick={() => onChange(store.id)}
                className={cn(
                  'flex items-center gap-3 rounded-xl border p-3 text-left transition-colors',
                  selected
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border bg-muted/20 text-muted-foreground hover:border-primary/50 hover:text-foreground',
                  disabled && 'cursor-not-allowed opacity-60',
                )}
                aria-pressed={selected}
              >
                <Store className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{storeLabel(store)}</span>
                  {store.nickname && store.storeUrl && (
                    <span className="block truncate text-xs text-muted-foreground">{store.storeUrl}</span>
                  )}
                </span>
                {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="text-sm font-medium text-foreground">{label}</div>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="bg-muted/50 border-border">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {stores.map((store) => (
            <SelectItem key={store.id} value={store.id}>
              {storeLabel(store)}
              {store.nickname && store.storeUrl ? ` — ${store.storeUrl}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}